import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Pressable,
  Spinner,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { FiEdit, FiMap } from "react-icons/fi";
import styles from "./styles.module.css";
import colors from "../../../../constants/colors";
import Location from "../../../../components/location";
import seat from "../../../../assets/icons/seat.svg";
import routes from "../../../../routes";
import Cell from "../../../../components/cell";
import constants from "../../../../constants";
import { StateContext, StatusContext } from "../../../../contexts";
import API from "../../../../api";
import QuotationFinishedModal from "../../../../components/quotationFinishedModal";
import Holder from "../../../../components/holder";
import Modal from "../../../../components/modal";
import Map from "../../../../components/map";
import { Marker, Polyline } from "@react-google-maps/api";
import decodePolyline from "decode-google-map-polyline";
import mapIcons from "../../../../constants/mapIcons";
import Vehicles from "../../../../components/vehicles";

function AgencyExperienceCreationDetailsPage() {
  const params = useParams();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const itineraryContainer = useRef();
  const [origin, setOrigin] = useState({
    ...constants.location,
    type: "origin",
  });
  const [boardingPoints, setBoardingPoints] = useState([]);
  const [intermediateDestinations, setIntermediateDestinations] = useState([]);
  const [destination, setDestination] = useState({
    ...constants.location,
    type: "destination",
  });
  const [returnOrigin, setReturnOrigin] = useState({
    ...constants.location,
    isReturn: true,
    type: "origin",
  });
  const [returnIntermediateDestinations, setReturnIntermediateDestinations] =
    useState([]);
  const [returnBoardingPoints, setReturnBoardingPoints] = useState([]);
  const [returnDestination, setReturnDestination] = useState({
    ...constants.location,
    isReturn: true,
    type: "destination",
  });
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [numberOfPassengers, setNumberOfPassengers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [holder, setHolder] = useState(null);
  const {
    isOpen: isMapModalOpen,
    onOpen: onMapModalOpen,
    onClose: onMapModalClose,
  } = useDisclose();
  const [map, setMap] = useState();
  const [returnMap, setReturnMap] = useState();
  const [itinerary, setItinerary] = useState([]);
  const [polyline, setPolyline] = useState("");
  const [returnPolyline, setReturnPolyline] = useState("");

  const getQuotation = useCallback(async () => {
    try {
      if (!params.id || !state.jwt) {
        return;
      }
      setIsLoading(true);
      const {
        data: {
          data: {
            itinerary: _itinerary,
            numberOfPassengers: _numberOfPassengers,
            holder: _holder,
            polyline: _polyline,
            returnPolyline: _returnPolyline,
          },
        },
      } = await API(state.jwt).get(`/quotations/${params.id}`);
      const _origin = _itinerary.find(
        (location) => !location.isReturn && location.type === "origin",
      );
      const _boardingPoints = _itinerary.filter(
        (location) => !location.isReturn && location.type === "boardingPoint",
      );
      const _intermediateDestinations = _itinerary.filter(
        (location) =>
          !location.isReturn && location.type === "intermediateDestination",
      );
      const _destination = _itinerary.find(
        (location) => !location.isReturn && location.type === "destination",
      );
      const _isRoundTrip = _itinerary.some((location) => location.isReturn);
      const _returnOrigin = _itinerary.find(
        (location) => location.isReturn && location.type === "origin",
      );
      const _returnIntermediateDestinations = _itinerary.filter(
        (location) =>
          location.isReturn && location.type === "intermediateDestination",
      );
      const _returnBoardingPoints = _itinerary.filter(
        (location) => location.isReturn && location.type === "boardingPoint",
      );
      const _returnDestination = _itinerary.find(
        (location) => location.isReturn && location.type === "destination",
      );
      setItinerary(_itinerary);
      setPolyline(_polyline);
      setReturnPolyline(_returnPolyline);
      setOrigin(_origin);
      setBoardingPoints(_boardingPoints);
      setIntermediateDestinations(_intermediateDestinations);
      setDestination(_destination);
      setIsRoundTrip(_isRoundTrip);
      if (_isRoundTrip) {
        setReturnOrigin(_returnOrigin);
        setReturnIntermediateDestinations(_returnIntermediateDestinations);
        setReturnBoardingPoints(_returnBoardingPoints);
        setReturnDestination(_returnDestination);
      }
      setNumberOfPassengers(_numberOfPassengers);
      setHolder(_holder);
    } catch (error) {
      if (error?.response?.data?.error?.details?.finished) {
        setFinished(true);
      }
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [params.id, state.jwt, status]);

  useEffect(() => {
    if (map) {
      const bounds = new window.google.maps.LatLngBounds();
      itinerary
        .filter((location) => !location.isReturn)
        .forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
      map.fitBounds(bounds);
    }
  }, [map, itinerary]);

  useEffect(() => {
    if (isRoundTrip && returnMap) {
      const bounds = new window.google.maps.LatLngBounds();
      itinerary
        .filter((location) => location.isReturn)
        .forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
      returnMap.fitBounds(bounds);
    }
  }, [isRoundTrip, returnMap, itinerary]);

  useEffect(() => {
    getQuotation();
  }, [getQuotation]);

  return (
    <VStack>
      <Helmet>
        <title>Confirmación de itinerario</title>
      </Helmet>
      <HStack paddingY={8} zIndex={1}>
        <Holder
          isLoading={isLoading}
          holder={holder}
          setHolder={setHolder}
          disabled
        />
      </HStack>
      <VStack space={8} paddingBottom={10}>
        <Heading color={colors.secondary} fontSize="2xl">
          Confirmación de itinerario
        </Heading>
        <VStack backgroundColor={colors.white} zIndex={1}>
          <HStack space={5} justifyContent="flex-end" paddingX={8}>
            <Link
              to={routes.agencyExperienceCreationId.replace(":id", params.id)}
              disabled={isLoading}>
              <HStack
                backgroundColor={colors.third}
                paddingX={5}
                padding={2}
                borderRadius="md"
                space={3}>
                <FiEdit color={colors.white} size={20} />
                <Text color={colors.white}>Editar</Text>
              </HStack>
            </Link>
          </HStack>
          <VStack ref={itineraryContainer} paddingX={8} paddingY={4}>
            <Text bold color={colors.primary} fontSize="lg">
              Ida
            </Text>
            <Divider backgroundColor={colors.primary} height={1} marginY={3} />
            {isLoading ? (
              <Center height={400}>
                <Spinner size="lg" color={colors.primary} />
              </Center>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <Cell header color={colors.third} />
                    <Cell header color={colors.third} />
                    <Cell header color={colors.third}>
                      Fecha de llegada
                    </Cell>
                    <Cell header color={colors.third}>
                      Hora de llegada
                    </Cell>
                    <Cell header color={colors.third}>
                      Estancia
                    </Cell>
                    <Cell header color={colors.third}>
                      Fecha de salida
                    </Cell>
                    <Cell header color={colors.third}>
                      Hora de salida
                    </Cell>
                    <Cell header color={colors.third}>
                      Observaciones
                    </Cell>
                  </tr>
                </thead>
                <tbody>
                  <Location
                    title="Origen"
                    numeral={1}
                    location={origin}
                    onChange={setOrigin}
                    hideArrivalDate
                    hideStay
                    readOnly
                    readOnlyDepartureDate
                    clearDisabled
                    readOnlyObservation
                  />
                  {boardingPoints.map((boardingPoint, index) => (
                    <Location
                      key={boardingPoint.key}
                      title={`Punto de abordaje #${index + 1}`}
                      numeral={index + 2}
                      location={boardingPoint}
                      readOnly
                      readOnlyDepartureDate
                      clearDisabled
                      readOnlyObservation
                    />
                  ))}
                  {intermediateDestinations.map(
                    (intermediateDestination, index) => (
                      <Location
                        key={intermediateDestination.key}
                        title={`Destino intermedio #${index + 1}`}
                        numeral={boardingPoints.length + index + 2}
                        location={intermediateDestination}
                        readOnly
                        readOnlyDepartureDate
                        clearDisabled
                        readOnlyObservation
                      />
                    ),
                  )}
                  <Location
                    title="Destino final"
                    numeral={
                      boardingPoints.length +
                      intermediateDestinations.length +
                      2
                    }
                    location={destination}
                    onChange={setDestination}
                    isLast
                    hideDepartureDate={!isRoundTrip}
                    hideStay={!isRoundTrip}
                    readOnly
                    readOnlyDepartureDate
                    clearDisabled
                    readOnlyObservation
                  />
                  {isRoundTrip && (
                    <>
                      <tr className={styles.roundTrip}>
                        <Cell bold color={colors.primary} fontSize="xl">
                          Vuelta
                        </Cell>
                        {Array.from(new Array(8)).map((_, index) => (
                          <Cell key={String(index)} />
                        ))}
                      </tr>
                      <Location
                        title="Origen"
                        numeral={1}
                        location={returnOrigin}
                        onChange={setReturnOrigin}
                        hideArrivalDate
                        hideStay
                        disabled
                        readOnlyDepartureDate
                        clearDisabled
                        readOnly
                        readOnlyObservation
                      />
                      {returnIntermediateDestinations.map(
                        (returnIntermediateDestination, index) => (
                          <Location
                            key={returnIntermediateDestination.key}
                            title={`Destino intermedio #${index + 1}`}
                            numeral={index + 2}
                            location={returnIntermediateDestination}
                            readOnly
                            readOnlyDepartureDate
                            clearDisabled
                            readOnlyObservation
                          />
                        ),
                      )}

                      {returnBoardingPoints.map(
                        (returnBoardingPoint, index) => (
                          <Location
                            key={returnBoardingPoint.key}
                            title={`Punto de descenso #${index + 1}`}
                            numeral={returnBoardingPoints.length + index + 2}
                            location={returnBoardingPoint}
                            readOnly
                            readOnlyDepartureDate
                            clearDisabled
                            readOnlyObservation
                          />
                        ),
                      )}
                      <Location
                        title="Final de viaje"
                        numeral={
                          returnBoardingPoints.length +
                          returnIntermediateDestinations.length +
                          2
                        }
                        location={returnDestination}
                        onChange={setReturnDestination}
                        isLast
                        hideDepartureDate
                        hideStay
                        disabled
                        clearDisabled
                        readOnly
                        readOnlyDepartureDate
                        readOnlyObservation
                      />
                    </>
                  )}
                </tbody>
              </table>
            )}
          </VStack>
        </VStack>
        <Text bold fontSize="sm" color={colors.gray} paddingLeft={8}>
          * La duración del viaje es estimada.
        </Text>
        <HStack backgroundColor={colors.white} padding={8} space={50}>
          <HStack space={5}>
            <Heading fontSize="lg" color={colors.third}>
              Tipo de viaje
            </Heading>
            <Text color={colors.gray}>
              {isLoading
                ? "..."
                : isRoundTrip
                ? "Ida y vuelta (viaje redondo)"
                : "Solo ida (viaje sencillo)"}
            </Text>
          </HStack>
          <HStack flex={1} space={5}>
            <Heading fontSize="lg" color={colors.third}>
              Número de pasajeros
            </Heading>
            <Image src={seat} alt="seat" width={7} height={7} />
            <Text color={colors.gray} fontSize="2xl">
              {isLoading ? "..." : numberOfPassengers}
            </Text>
          </HStack>
          <Pressable
            backgroundColor={colors.third}
            paddingX={4}
            paddingY={2}
            borderRadius={5}
            onPress={onMapModalOpen}
            disabled={isLoading}>
            <HStack space={2}>
              <FiMap color={colors.white} size={20} />
              <Text color={colors.white}>Ver en mapa</Text>
            </HStack>
          </Pressable>
          <HStack space={5}>
            <Link
              to={routes.agencyExperienceCreationTransportSelection.replace(
                ":id",
                params.id,
              )}
              disabled={isLoading}>
              <Box
                backgroundColor={colors.primary}
                paddingY={3}
                paddingX={100}
                borderRadius="full">
                <Text color={colors.white}>Confirmar viaje</Text>
              </Box>
            </Link>
          </HStack>
        </HStack>
        <Vehicles />
      </VStack>
      <QuotationFinishedModal isOpen={finished} />
      <Modal
        maxWidth={900}
        isOpen={isMapModalOpen}
        onRequestClose={onMapModalClose}>
        <VStack padding={5}>
          {isRoundTrip && (
            <Text bold color={colors.primary} fontSize="2xl">
              Ida
            </Text>
          )}
          <Box height={400} width="full">
            <Map setRef={setMap}>
              {Boolean(polyline) && (
                <Polyline
                  path={decodePolyline(polyline)}
                  options={{
                    strokeColor: colors.primary,
                  }}
                />
              )}
              {itinerary
                .filter((location) => !location.isReturn)
                .map((location) => (
                  <Marker
                    key={location.id}
                    position={{
                      lat: location.latitude,
                      lng: location.longitude,
                    }}
                    icon={mapIcons[location.type]}
                    title={location.name}
                  />
                ))}
            </Map>
          </Box>
          {isRoundTrip && (
            <VStack marginTop={5}>
              <Text bold color={colors.primary} fontSize="2xl">
                Vuelta
              </Text>
              <Box height={400} width="full">
                <Map setRef={setReturnMap}>
                  {Boolean(returnPolyline) && (
                    <Polyline
                      path={decodePolyline(returnPolyline)}
                      options={{
                        strokeColor: colors.primary,
                      }}
                    />
                  )}
                  {itinerary
                    .filter((location) => location.isReturn)
                    .map((location) => (
                      <Marker
                        key={location.id}
                        position={{
                          lat: location.latitude,
                          lng: location.longitude,
                        }}
                        icon={mapIcons[location.type]}
                        title={location.name}
                      />
                    ))}
                </Map>
              </Box>
            </VStack>
          )}
        </VStack>
      </Modal>
    </VStack>
  );
}

export default AgencyExperienceCreationDetailsPage;
